import React, { useCallback, useRef, useState } from 'react';
import { DndContext, pointerWithin, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { MobileSidebarProvider } from '../contexts/MobileSidebarContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useViewport } from '../components/hooks/useViewport';
import { useDossier } from './DossierContext';
import { useChapterManagement } from './hooks/useChapterManagement';
import { useDndKitManagement } from './hooks/useDndKitManagement';
import DossierEditorPane from './DossierEditorPane';
import DossierDragOverlay from './DossierDragOverlay';
import TopBarDossier from '../components/TopBarDossier';
import DossierSidebar from '../components/DossierSidebar';
import DossierSettingsModal from '../components/DossierSettingsModal';
import JuridicalReferencesModal from '../components/JuridicalReferencesModal';

/**
 * Shell de l'éditeur de dossier : sidebar à gauche, top bar + pane à
 * droite. C'est le composant qui monte `DndContext` et qui pilote
 * l'ouverture des modaux (settings + références juridiques).
 *
 * Les composants legacy `TopBarDossier`, `DossierSidebar` et
 * `DossierSettingsModal` continuent à recevoir leurs props habituelles
 * — le layout fait l'adaptation depuis le ctx. Leur refactor en
 * consumers natifs du ctx pourra se faire dans un chantier ultérieur.
 */
const DossierEditorLayout: React.FC = () => {
  const {
    dossier,
    dossierId,
    userRole,
    currentChapter,
    currentChapterIndex,
    setCurrentChapterIndex,
    isEditing,
    hasUnsavedChanges,
    isSaving,
    lastSaved,
    save,
    replaceDossier,
    enterPreview,
  } = useDossier();
  const { isMobile } = useViewport();
  const { width: sidebarWidth, isCollapsed: isSidebarCollapsed } = useSidebar();

  const { addChapter, editChapter, deleteChapter, reorderChapters } = useChapterManagement();
  const dndOps = useDndKitManagement();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const editorContainerRef = useRef<HTMLDivElement>(null);

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsModalTab, setSettingsModalTab] = useState<'Général' | 'Auteurs'>('Général');
  const [showReferencesModal, setShowReferencesModal] = useState(false);

  const toggleSettingsModal = useCallback(
    (tab: 'Général' | 'Auteurs' | string = 'Général') => {
      setSettingsModalTab((tab === 'Auteurs' ? 'Auteurs' : 'Général') as 'Général' | 'Auteurs');
      setSettingsModalOpen(prev => !prev);
    },
    []
  );

  const handleSettingsSave = useCallback(
    async (updatedDossier: typeof dossier) => {
      replaceDossier(updatedDossier);
      await save();
    },
    [replaceDossier, save]
  );

  const handleChapterTitleChange = useCallback(
    (newTitle: string) => {
      editChapter(currentChapterIndex, newTitle);
    },
    [editChapter, currentChapterIndex]
  );

  const handleDossierTitleChange = useCallback(
    (newTitle: string) => {
      replaceDossier({ ...dossier, title: newTitle });
    },
    [dossier, replaceDossier]
  );

  // Cas spécial : dossier sans aucun chapitre. On affiche un écran
  // minimal qui invite à créer le premier chapitre. Le drag-and-drop
  // n'est pas monté car il n'y a rien à drag.
  if (dossier.content.length === 0) {
    return (
      <MobileSidebarProvider>
        <div className="min-h-screen bg-gray-100 flex flex-col">
          <TopBarDossier
            dossierId={dossierId}
            toggleSettingsModal={toggleSettingsModal}
            isSettingsModalOpen={settingsModalOpen}
            title={dossier.title}
            onTitleChange={handleDossierTitleChange}
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            handleSave={save}
            lastSaved={lastSaved ?? new Date()}
            userRole={userRole}
            onShowReferences={() => setShowReferencesModal(true)}
            onVersionPreview={enterPreview}
            onRestoreComplete={d => d && replaceDossier(d)}
          />
          <div className="flex-grow flex justify-center items-center mt-16">
            <button onClick={addChapter}>Ajouter un chapitre</button>
          </div>
        </div>
      </MobileSidebarProvider>
    );
  }

  const sidebarProps = {
    title: dossier.title,
    onTitleChange: handleDossierTitleChange,
    isEditing,
    userRole,
    dossierContent: dossier,
    onOpenSettingsModal: () => toggleSettingsModal('Général'),
    onAddChapter: addChapter,
    onEditChapter: editChapter,
    onDeleteChapter: deleteChapter,
    onReorderChapters: reorderChapters,
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={dndOps.handleDragStart}
      onDragOver={dndOps.handleDragOver}
      onDragEnd={dndOps.handleDragEnd}
    >
      <MobileSidebarProvider>
        <div className="h-screen flex overflow-hidden bg-mcdm-primary-200 dark:bg-mcdm-third-500">
          {!isMobile && (
            <DossierSidebar
              {...sidebarProps}
              width={sidebarWidth}
              isCollapsed={isSidebarCollapsed}
            />
          )}
          {isMobile && <DossierSidebar {...sidebarProps} />}

          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <TopBarDossier
              dossierId={dossierId}
              toggleSettingsModal={toggleSettingsModal}
              isSettingsModalOpen={settingsModalOpen}
              title={currentChapter?.title || ''}
              onTitleChange={handleChapterTitleChange}
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              handleSave={save}
              lastSaved={lastSaved ?? new Date()}
              userRole={userRole}
              onShowReferences={() => setShowReferencesModal(true)}
              onVersionPreview={enterPreview}
              onRestoreComplete={d => d && replaceDossier(d)}
            />

            <DossierEditorPane editorContainerRef={editorContainerRef} />
          </div>

          <DossierDragOverlay
            activeId={dndOps.activeId as any}
            activeType={dndOps.activeType}
            activeItem={dndOps.activeItem}
            activeOverId={dndOps.activeOverId as any}
          />
        </div>
      </MobileSidebarProvider>

      <DossierSettingsModal
        isOpen={settingsModalOpen}
        onClose={toggleSettingsModal}
        dossier={dossier}
        onSave={handleSettingsSave}
        initialTab={settingsModalTab}
        userRole={userRole}
      />

      {showReferencesModal && (
        <JuridicalReferencesModal
          isOpen={showReferencesModal}
          onClose={() => setShowReferencesModal(false)}
          dossierContent={dossier}
          currentChapterIndex={currentChapterIndex}
          setCurrentChapterIndex={setCurrentChapterIndex}
        />
      )}
    </DndContext>
  );
};

export default DossierEditorLayout;
